import * as React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';

import logoLight from '../../../assets/logo-light.png';
import logoDark from '../../../assets/logo-dark.png';
import aiChevron from '../../../assets/ai-chevron.svg';

import SearchAppBar from '../Search';
import DrawerMenu from './DrawerMenu';
import UniversalSearch from '../UniversalSearch';
import { LoginForm } from '../Login';
import { RegisterForm } from '../Register';
import RecommendationQuiz from '../RecommendationQuiz';

import { useAuth } from '../../../context/Auth.context';
import './styles.css';

// Wide enough for a three-across tile grid; the old 240 fitted a text list.
const drawerWidth = 320;

// Full-bleed: header content spans the full width to match the un-capped page
// content (the 68rem body cap was removed). The toolbar keeps its own padding.
const contentMaxWidth = '100%';

function DrawerAppBar(props) {
  const { user, onLogout } = useAuth();
  const { window, onChange, mode } = props;

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openLogin, setOpenLogin] = React.useState(false);
  const [openRegister, setOpenRegister] = React.useState(false);
  const [appUser, setAppUser] = React.useState(null);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [openRecommendations, setRecommendations] = React.useState(false);

  React.useEffect(() => {
    setAppUser(user);
    if (user) {
      setOpenLogin(false);
    }
  }, [user]);

  const openSearch = () => setSearchOpen((prev) => !prev);
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const openLoginForm = (value) => setOpenLogin(value);
  const handleLogout = () => onLogout();
  const toggleRecommendations = () => setRecommendations((prev) => !prev);

  const logo = mode === 'light' ? logoDark : logoLight;

  const drawer = (
    <DrawerMenu
      logo={logo}
      mode={mode}
      onToggleMode={onChange}
      onClose={handleDrawerToggle}
      user={appUser}
      onLogout={() => { handleLogout(); setMobileOpen(false); }}
      onSignIn={() => { setMobileOpen(false); openLoginForm(true); }}
      onRegister={() => { setMobileOpen(false); setOpenRegister(true); }}
    />
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        component="nav"
        aria-label="Main navigation"
        position="fixed"
        elevation={0}
        sx={{
          color: 'text.primary',
          // Frosted glass: a mostly-opaque themed surface (0.8) keeps text
          // contrast high in both colour modes, with a blur over scrolled content.
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            width: '100%',
            maxWidth: contentMaxWidth,
            mx: 'auto',
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <Link to="/" aria-label="Vhakacha home" style={{ display: 'inline-flex' }}>
              <Box
                component="img"
                src={logo}
                alt="Vhakacha"
                loading="lazy"
                sx={{ height: 40, width: 'auto', display: 'block' }}
              />
            </Link>
          </Box>

          {/* Truly centred: the logo and the button cluster each take `flex: 1`,
              so they claim equal shares of the leftover space and this sits on
              the bar's midline — not merely in the middle of what the wider
              right-hand cluster leaves over. */}
          <Box sx={{ flex: '0 1 640px', minWidth: 0, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', px: 2 }}>
            <UniversalSearch />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: { xs: 0.5, sm: 1 } }}>
            {/* Below md the bar has nowhere to go without crushing the logo and
                the Let's Go pill, so it moves into the full-screen sheet this
                icon opens. Same component, same behaviour — a traveller on a
                phone is the one most likely to be searching for a name. */}
            <IconButton
              color="inherit"
              onClick={openSearch}
              aria-label="Search listings"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <SearchIcon />
            </IconButton>

            <Button
              onClick={toggleRecommendations}
              startIcon={
                <Box component="img" src={aiChevron} alt="" sx={{ width: '1.1em', height: '1.1em' }} />
              }
              sx={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                // "Neon" pill with the coral brand colour in the border + glow.
                // Dark mode: white label on a near-black fill. Light mode: no fill +
                // dark label (so it doesn't read as a heavy black chip on the light bar).
                // The label is white-on-dark / dark-on-light — both AA-clean and
                // independent of the white-on-orange exception.
                color: (theme) => (theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary),
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1B1E24' : 'transparent'),
                border: '1.5px solid',
                borderColor: 'primary.main',
                borderRadius: 999,
                px: 2,
                py: 0.75,
                boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.55)}`,
                transition: (theme) => theme.transitions.create(['box-shadow', 'border-color', 'background-color']),
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#1B1E24' : alpha(theme.palette.primary.main, 0.08),
                  borderColor: 'primary.light',
                  boxShadow: (theme) => `0 0 14px ${alpha(theme.palette.primary.main, 0.85)}`,
                },
              }}
            >
              Let's Go
            </Button>

            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" aria-label="Mobile navigation">
        <Drawer
          container={container}
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            // Above the app bar, which sits at drawer + 1. The menu carries its
            // own logo and close button at the top, and the bar was painting
            // over both — the old text-list drawer avoided that by starting its
            // content 80px down, which this layout has no room for.
            zIndex: (theme) => theme.zIndex.drawer + 2,
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <SearchAppBar open={searchOpen} handleClose={openSearch} />
      <LoginForm open={openLogin} setOpen={setOpenLogin} />
      <RegisterForm open={openRegister} setOpen={setOpenRegister} />
      <RecommendationQuiz open={openRecommendations} handleClick={toggleRecommendations} />
    </Box>
  );
}

DrawerAppBar.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

export default DrawerAppBar;
